import { Text } from '@noorden-insurtech/alba-design-system';
import { useState, type ReactNode } from 'react';

/** Configuração de uma aba do {@link AnalysisTabsCard}. */
export type AnalysisTabItem = {
  /** Identificador único da aba. */
  id: string;
  /** Texto exibido no topo da aba (qualquer texto). */
  label: string;
  /** Quantidade exibida como badge ao lado do label (opcional). */
  count?: number;
  /** Ícone opcional exibido à direita do label. */
  icon?: ReactNode;
  /** Conteúdo renderizado quando a aba está ativa. */
  content: ReactNode;
};

type Props = {
  /** Abas a serem exibidas. */
  tabs: AnalysisTabItem[];
  /** Id da aba inicialmente ativa (padrão: a primeira). */
  defaultTabId?: string;
  /** Classes extras para o card. */
  className?: string;
};

/**
 * Card reutilizável de abas para telas de análise.
 *
 * Recebe um array de abas — cada uma com um texto livre (`label`), uma
 * quantidade opcional (`count`), um ícone opcional e o `content` a renderizar.
 * Não conhece nada do domínio PME: pode ser reaproveitado em qualquer análise
 * passando outras abas/conteúdos.
 */
export function AnalysisTabsCard({ tabs, defaultTabId, className }: Props) {
  const [activeId, setActiveId] = useState(defaultTabId ?? tabs[0]?.id);
  const activeTab = tabs.find(tab => tab.id === activeId) ?? tabs[0];

  if (!activeTab) return null;

  return (
    <div
      className={`flex w-full flex-col rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6 ${className ?? ''}`}
    >
      <div
        role='tablist'
        className='flex items-center gap-10 overflow-x-auto border-b border-alba-gray-200 sm:gap-16'
      >
        {tabs.map(tab => {
          const isActive = tab.id === activeTab.id;
          return (
            <button
              key={tab.id}
              type='button'
              role='tab'
              aria-selected={isActive}
              onClick={() => setActiveId(tab.id)}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-4 pb-3 font-montserrat text-sm transition-colors ${
                isActive
                  ? 'border-alba-blue-700 font-bold text-alba-blue-700'
                  : 'border-transparent text-alba-gray-500 hover:text-alba-black-500'
              }`}
            >
              <span className='whitespace-nowrap'>{tab.label}</span>
              {typeof tab.count === 'number' && (
                <span
                  className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                    isActive
                      ? 'bg-alba-blue-100 text-alba-blue-700'
                      : 'bg-alba-gray-100 text-alba-gray-600'
                  }`}
                >
                  {tab.count}
                </span>
              )}
              {tab.icon && (
                <span className='flex items-center text-current'>
                  {tab.icon}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div role='tabpanel' className='pt-6'>
        {activeTab.content}
      </div>
    </div>
  );
}

/** Cabeçalho de seção usado como fallback quando uma aba não tem dados. */
export function AnalysisTabEmptyState({ message }: { message: string }) {
  return (
    <Text className='py-8 text-center font-montserrat text-sm text-alba-gray-600'>
      {message}
    </Text>
  );
}

/** Linhas pulsando no mesmo grid da tabela da aba, enquanto os dados carregam. */
export function AnalysisTabSkeleton({
  gridClassName,
  columns,
  rows = 3,
}: {
  /** Classe `grid-cols-*` da tabela, para o skeleton ocupar as mesmas colunas. */
  gridClassName: string;
  columns: number;
  rows?: number;
}) {
  return (
    <div aria-busy='true'>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className={`grid ${gridClassName} gap-4 py-3`}>
          {Array.from({ length: columns }).map((_, cellIndex) => (
            <div
              key={cellIndex}
              className='h-3 animate-pulse rounded bg-alba-gray-200'
              style={{ width: `${55 + ((index + cellIndex) % 4) * 12}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
